import { useState, useEffect, useRef, useCallback } from 'react';
import { Drawer } from '@mui/material';
import useInfiniteScroll from '../../../../hook/useInfiniteScroll';
import toast from 'react-hot-toast';
import PostCard from './PostCard';
import PostDetail from './PostDetail';
import OptionDialog from '../OptionDialog';
import { useMyFreeboardListQuery, useDeleteMyFreeboardMutation } from '../../../../queries';

const Sort = {
  Registration: 1,
  Latest: 2,
};

const LIMIT = 10;

function FreeBoard() {
  const pushedRef = useRef(false);
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [list, setList] = useState([]);
  const [contentOpenId, setContentOpenId] = useState(null);
  const [sorting] = useState(Sort.Latest);
  const prevSortingRef = useRef(Sort.Latest);
  const [optionOpenId, setOptionOpenId] = useState(null);

  const { data: qdata, isFetching } = useMyFreeboardListQuery(
    {
      limit: LIMIT,
      page,
    },
    { enabled: true }
  );

  const { mutate: deleteMyFreeboard } = useDeleteMyFreeboardMutation();

  useEffect(() => {
    if (qdata?.posts) {
      if (page === 1) {
        setList(qdata.posts);
      } else {
        setList((prev) => {
          const existingIds = new Set(prev.map((item: any) => item.id));
          const newPosts = qdata.posts.filter((item: any) => !existingIds.has(item.id));
          return [...prev, ...newPosts];
        });
      }
      setTotalCount(qdata.total);
    }
  }, [qdata, page]);

  const refreshList = () => {
    setPage(1);
    setList([]);
    setHasMore(true);
  };

  // 등록순/최신순
  useEffect(() => {
    if (prevSortingRef.current === sorting) return;
    refreshList();
    prevSortingRef.current = sorting;
  }, [prevSortingRef, sorting]);

  // infinite scroll
  const { lastItemRef } = useInfiniteScroll({
    hasMore,
    isLoading: isFetching,
    onLoadMore: () => {
      if (totalCount <= page * LIMIT) {
        setHasMore(false);
        return;
      }
      const newPage = page + 1;
      setPage(newPage);
    },
  });

  // 뒤로가기에 드로워가 닫히도록 설정
  const openDrawer = useCallback((id) => {
    setContentOpenId(id);
    window.history.pushState({ drawer: true }, '');
    pushedRef.current = true;
  }, []);

  const closeDrawer = useCallback(() => {
    setContentOpenId(null);
    if (pushedRef.current) {
      pushedRef.current = false;
      window.history.back();
    }
  }, []);

  useEffect(() => {
    const onPopState = () => {
      if (contentOpenId !== null) {
        setContentOpenId(null);
      }
    };
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, [contentOpenId]);

  const handleDeletePost = useCallback(() => {
    if (optionOpenId === null) return;
    deleteMyFreeboard(optionOpenId, {
      onSuccess: () => {
        setOptionOpenId(null);
        refreshList();
      },
      onError: (error) => {
        console.error('Failed to delete comment', error);
        toast.error('1:1문의 글 삭제에 실패했습니다.');
      },
    });
  }, [optionOpenId, deleteMyFreeboard, refreshList]);


  return (
    <div className="relative w-full">
      {/* <div className="w-full flex justify-end items-center my-5 text-[12px] font-[700] gap-2">
        <button
          type="button"
          style={{ color: sorting === Sort.Registration ? '#363636' : '#969696' }}
          onClick={() => setSorting(Sort.Registration)}
        >
          등록순
        </button>
        <button
          type="button"
          style={{ color: sorting === Sort.Latest ? '#363636' : '#969696' }}
          onClick={() => setSorting(Sort.Latest)}
        >
          최신순
        </button>
      </div> */}
      <div className="w-full pt-5">
        {list?.length >= 1 &&
          list?.map((content, idx) => {
            return (
              <PostCard
                key={content.id}
                post={content}
                onClick={(id) => openDrawer(id)}
                onOptionClick={(id) => setOptionOpenId(id)}
                ref={idx === list.length - 1 ? lastItemRef : undefined}
              />
            );
          })}
      </div>
      <OptionDialog
        open={optionOpenId !== null}
        onClose={() => setOptionOpenId(null)}
        onDeleteClick={handleDeletePost}
      />
      <Drawer
        open={contentOpenId !== null}
        onClose={closeDrawer}
        anchor="right"
        sx={{
          '& .MuiDrawer-paper': {
            width: '100vw',
            maxWidth: '100vw',
          },
        }}
      >
        <PostDetail
          drawerOpen={contentOpenId !== null}
          onClickPrev={closeDrawer}
          postId={contentOpenId}
        />
      </Drawer>
    </div>
  );
}

export default FreeBoard;
